import { IconType } from "../../../icons/Icon";
import { DefaultButton } from "../../button/defaultButton/DefaultButton";
import { DismissibleNote } from "../../common/DismissibleNote/DismissibleNote";
import { EmptyStateCard } from "../../common/EmptyStateCard/EmptyStateCard";
import { RenderWhen } from "../../../infrastructure/components/RenderWhen";
import { IBodyMetricsDataSource } from "../models/bodyMetricModels";
import { useBodyMetricsOverview } from "../hooks/useBodyMetricsOverview";
import { BodyMetricCard } from "../components/BodyMetricCard/BodyMetricCard";
import { AddBodyMetricDialog } from "../components/AddBodyMetricDialog/AddBodyMetricDialog";
import { BodyMetricDetailDialog } from "../components/BodyMetricDetailDialog/BodyMetricDetailDialog";
import styles from "./BodyMetricsOverview.module.scss";

interface IBodyMetricsOverviewProps {
  dataSource: IBodyMetricsDataSource;
  /** Shown above the cards, e.g. whose metrics these are on the staff page. */
  subtitle?: string;
  /**
   * Short explanation of the page, shown above the cards. The storageKey remembers that
   * the user closed it, so it never comes back.
   */
  intro?: { storageKey: string; title: string; text: string };
}

/**
 * The body-metrics experience shared by the user's own "Моите метрики" page and the
 * staff page on a user's profile, the way WorkoutsOverview is for workouts: one card per
 * measured metric, "+" to record a value, and a metric's details with its charts.
 */
export const BodyMetricsOverview = ({ dataSource, subtitle, intro }: IBodyMetricsOverviewProps) => {
  const {
    summaries,
    isLoading,
    hasAny,
    isAddOpen,
    addPresetType,
    openAdd,
    closeAdd,
    openSummary,
    openDetails,
    closeDetails,
  } = useBodyMetricsOverview(dataSource);

  return (
    <div className={styles.root}>
      <div className={styles.root_content}>
        <RenderWhen condition={!!intro}>
          <DismissibleNote
            storageKey={intro?.storageKey ?? ""}
            title={intro?.title ?? ""}
            text={intro?.text ?? ""}
          />
        </RenderWhen>

        <div className={styles.root_toolbar}>
          <span className={styles.root_toolbar_subtitle}>{subtitle}</span>
          <RenderWhen condition={hasAny}>
            <DefaultButton
              className={styles.root_toolbar_add}
              design="outline"
              label="+ Добави метрика"
              width="auto"
              onClick={() => openAdd()}
            />
          </RenderWhen>
        </div>

        <RenderWhen condition={isLoading}>
          <div className={styles.root_grid}>
            {Array(4)
              .fill(0)
              .map((_, index) => (
                <div className={styles.root_skeleton} key={index} />
              ))}
          </div>
        </RenderWhen>

        <RenderWhen condition={!isLoading && !hasAny}>
          <EmptyStateCard
            className={styles.root_empty}
            icon={IconType.Statistics}
            title="Все още няма метрики"
            description="Записвайте тегло, ръст, телесни мазнини, обиколки и още - и следете как се променят във времето."
            actionLabel="+ Добави метрика"
            onAction={() => openAdd()}
          />
        </RenderWhen>

        <RenderWhen condition={!isLoading && hasAny}>
          <div className={styles.root_grid}>
            {summaries.map((summary) => (
              <BodyMetricCard
                key={summary.type}
                summary={summary}
                onClick={() => openDetails(summary.type)}
              />
            ))}
          </div>
        </RenderWhen>
      </div>

      <BodyMetricDetailDialog
        summary={openSummary}
        dataSource={dataSource}
        onHide={closeDetails}
        onAdd={openAdd}
      />

      <AddBodyMetricDialog
        visible={isAddOpen}
        presetType={addPresetType}
        dataSource={dataSource}
        onHide={closeAdd}
      />
    </div>
  );
};
